import * as React from 'react';
import _ from 'lodash';
import { CampaignNodes } from 'omega/types/ts/campaigns';
import { Grid, Modal, Header } from 'semantic-grab-ui/src';
import {
  getScenarioConstraints, getCountConditionConstraints,
  getTimeConstraints, getDelayConstraints,
} from 'components/form/trident-campaigns/constraints';
import { NodeOccMap } from 'types/trident-campaigns';
import { Campaign } from 'types/ts/campaigns';
import { nodeTypeToAbbr } from 'data/trident-campaigns';
import { nodeTypes } from 'data/trident-types';

export const getModal = (title: string, WidgetForm: React.ElementType, widgetFormProps) => {
  return (
    <Modal size="fullscreen" open>
      <Modal.Header>
        <Grid columns="equal">
          <Grid.Column>
            <Header as="h2">{title}</Header>
          </Grid.Column>
        </Grid>
      </Modal.Header>
      <Modal.Content scrolling>
        <WidgetForm {...widgetFormProps} />
      </Modal.Content>
    </Modal>
  );
};

export const GET_CONSTRAINTS_FUNCTION = {
  scenarios: getScenarioConstraints,
  time: getTimeConstraints,
  countCondition: getCountConditionConstraints,
  delay: getDelayConstraints,
};

export const MODAL_TYPE = {
  actions: 'action',
  scenarios: 'scenario',
  count: 'counter',
  time: 'timeTrigger',
  condition: 'condition',
  countCondition: 'countCondition',
  delay: 'delay',
  experiment: 'experiment',
};

export const INITIAL_NODE = {
  scenarios: {
    type: 'scenarios',
    data: {
      scenarios: [
        {},
      ],
    },
    autoHoldbackEnabled: true,
  },
  time: {
    type: 'time',
    data: {
      frequency: 'nr',
    },
  },
  schedule: {
    type: 'schedule',
    data: {
      frequency: 'nr',
    },
    autoHoldbackEnabled: true,
  },
  enumeration: {
    type: 'enumeration',
    data: {
      type: 'segment',
      payload: {
        names: [''],
      },
    },
  },
  condition: {
    type: 'condition',
    data: {
    },
  },
  count: {
    type: 'count',
    data: {
    },
  },
  countCondition: {
    type: 'countCondition',
    data: {
    },
  },
  actions: {
    type: 'actions',
    data: [],
  },
  delay: {
    type: 'delay',
    data: {
    },
  },
  experiment: {
    type: 'experiment',
    data: {
    },
  },
  randomizer: {
    type: 'randomizer',
    data: {
    },
  },
  experimentTreatment: {
    type: 'experimentTreatment',
    data: {
      groupPercentage: [],
    },
  },
};

export const spanOn = (curr: number, parsed: CampaignNodes) => {
  if (!parsed[curr]) {
    return 0;
  }
  if (!parsed[curr].children) {
    return 1;
  }
  return parsed[curr].children.reduce((currentValue, id) => {
    return currentValue + parseInt(spanOn(Number(id), parsed), 10);
  }, 0);
};

export const flatten = (
  nodes: CampaignNodes,
  curr: string,
  currArr: number[],
  mainArr: number[][],
) => {
  if (!nodes) {
    return;
  }
  if (!(nodes[curr] && nodes[curr].children)) {
    mainArr.push(currArr);
    return;
  }

  nodes[curr].children.forEach((child, index) => {
    let newArr = currArr;
    if (index > 0) {
      newArr = currArr.map(item => {
        return item > 0 ? -1 * item : item;
      });
    }
    newArr = newArr.concat(parseInt(child, 10));
    flatten(nodes, child, newArr, mainArr);
  });
  return mainArr;
};

// getDefaultNodeName returns a default name to be displayed on the node widget
export const getDefaultNodeName = (nodeKey: string, nodeType: string, nodeOccMap: NodeOccMap) => {
  const nodesOcc = _.get(nodeOccMap, nodeType, []);
  const nameData = nodesOcc.find(x => x.nodeID === nodeKey);
  const prefix = _.get(nodeTypeToAbbr, nodeType, '');
  return `${prefix}${_.get(nameData, 'key', '')}`;
};

// getStartingNodeOptions returns a list of starting node types
export function getStartingNodeOptions(listRuleTypes: Object, targetUserType: string) {
  const startingNodes = [];
  const eventTypesObjectArray = _.get(listRuleTypes, targetUserType, []);
  const hasTimeEvent = eventTypesObjectArray.some(event => event.eventType === 'onDateAndTime');
  const hasScenarioEvent = eventTypesObjectArray.some(event => event.eventType !== 'onDateAndTime');
  if (hasScenarioEvent) {
    startingNodes.push(nodeTypes.scenarios);
  }
  if (hasTimeEvent) {
    startingNodes.push(nodeTypes.time);
  }
  return startingNodes;
}

// getNodePopupContent returns pop-up text which contains node name, treatment, stats key info
export function getNodePopupContent(model: Campaign, pathToName: string, nodeKey: string, defaultName: string, isObjectResult = false) {
  const objResult = { name: `${_.get(model.nodes[nodeKey], pathToName, defaultName) || defaultName}`, value:'' };
  const nodeType = _.get(model.nodes[nodeKey], 'type', '');

  // add tm/tg/schedule mapping info
  if (nodeType === nodeTypes.scenarios || nodeType === nodeTypes.time) {
    const tgID = _.get(model, `rootNodeTmGroupMapping[${nodeKey}]`, 0);
    if (tgID > 0) {
      objResult.value = `(tg: ${tgID})`;
    }
  } else if (nodeType == nodeTypes.schedule) {
    const scheduleID = _.get(model, `rootNodeScheduleMapping[${nodeKey}]`, 0);
    objResult.value = `(schedule: ${scheduleID})`;
  } else {
    const tmIDs = _.get(model, `nodeTreatmentMapping[${nodeKey}]`, []);
    if (tmIDs.length) {
      objResult.value = `(tm: ${_.join(tmIDs, ',')})`;
    }
  }

  if (!isObjectResult){
    return `${objResult.name} ${objResult.value}`
  }

  return objResult;
}

// getTreatmentIdFromScenarioNode returns the treatmentID's related to the given scenario
export function getTreatmentIdsFromScenarioNode(model: Campaign, scenariosKey: string, scenarioKey: string) {
  const nodeKey = `${scenariosKey}-${scenarioKey}`;
  return _.get(model, `nodeTreatmentMapping[${nodeKey}]`, []);
}

// findNodes starts traversing the `nodes` from the root node, and returns array of nodes that `match` gives true output
export function findNodes(rootNodeID: string, nodes: Object, match: Function) {
  const results = [];
  findNodesHelper(rootNodeID, nodes, match, results);
  return results;
}

function findNodesHelper(rootNodeID: string, nodes: Object, match: Function, results: Array<Node>) {
  const rootNode = _.get(nodes, rootNodeID);
  if (_.isEmpty(rootNode)) {
    return;
  }
  if (match(rootNode)) {
    results.push({
      ...rootNode,
      id: rootNodeID,
    });
  }

  _.map(rootNode.children, childID => {
    findNodesHelper(childID, nodes, match, results);
  });
}

// returns true if scrolled successfully else returns false
export const scrollToElement = (adjustTopOffset: number, elementID: string) => {
  // domElement.getBoundingClientRect().top => top position of the element with respect to top left corner of viewport, will change as page is scrolled
  // domElement.offsetTop => the top position (in pixels) relative to the parent
  // window.scrollY => the number of pixels that the document is currently scrolled vertically.
  // domElement.getBoundingClientRect().top + window.scrollY == domElement.offsetTop
  // domElement.scrollIntoView() is not used as positioning becomes incorrect because of the static header at the top
  let element = document.getElementById(elementID)
  if(element) {
    window.scroll({top:element.offsetTop + adjustTopOffset, behavior:'smooth'})
    return true
  }
  return false
}
